import type {
  ProductEditorForm,
  ProductEditorSyncValidator,
  ProductEditorAsyncValidator,
} from './ProductForm.types'
import type { AppLinkProps } from '@/hooks/usePathHelper'
import type { ProductRole } from '@/lib/product-role'
import type { Product, SubscriptionFrequency } from '@/lib/services/products/products.types'
import type {
  ProductEditorValues,
  ProductEditorVariant,
  ProductEditorCollection,
  ProductEditorAddOnSet,
  ProductFormValues,
  ProductFormSource,
} from '@/lib/services/products/products.form'
import type { ProductPriceAdjustment } from '@/lib/services/price-adjustments/priceAdjustments.types'
import type { Collection } from '@/lib/services/collections/collections.types'
import type { MediaFile } from '@/lib/services/media/media.types'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import type { DragEndEvent } from '@dnd-kit/core'
import type { LucideIcon } from 'lucide-react'
import type { LightboxItem } from '@/components/media/ImageLightboxGallery'
import type { MouseEvent, ReactNode } from 'react'
import { ProductMoreActionsMenu } from '@/components/products/ProductMoreActionsMenu'
import { ProductSummarySidebar } from '@/components/products/ProductSummarySidebar'
import { ProductVisibilityPicker } from '@/components/products/ProductVisibilityPicker'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { Can, useAbility, useIsReadOnly } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import { getAssetMedia } from '@/lib/media-editing'
import { normalizeRichTextHtml, plainTextToRichTextHtml } from '@/lib/rich-text'
import {
  PhotoManagerDefaultUploadDestination,
  PhotoManagerSelectionMode,
} from '@/lib/photo-manager-config'
import { canTrackInventoryForLocations } from '@/lib/services/products/products.inventory'
import {
  createProductRoleTransitionMemory,
  defaultSubscriptionDisclaimer,
  transitionProductRole,
} from '@/lib/services/products/products.role-transition'
import { useMerchandisingTagGroups } from '@/lib/services/merchandising-tags/merchandising-tags.query'
import {
  flattenMerchandisingTagGroups,
  splitMerchandisingTagIds,
} from '@/lib/services/merchandising-tags/merchandising-tags.utils'
import { getDraftProductActivePriceAdjustment } from '@/lib/services/price-adjustments/priceAdjustments.summary'
import { getProductRole } from '@/lib/product-role'
import { findSubscriptionPackageCollection } from '@/lib/subscription-package-collection'
import {
  areSameIdList,
  buildProductSearchTagRecommendationDraft,
  createCopiedVariant,
  createEmptyVariant,
  createStandardDeluxePremiumVariants,
  emptyProduct,
  getFirstInvalidVariantIndex,
  getMediaId,
  getPreferredActiveVariantId,
  getProductRoleLabel,
  getProductValidationToastDescription,
  getVariantLabel,
  getVariantWorkspaceSignature,
  isAddOnProductRole,
  isBlankVariant,
  isSubscriptionPackageRole,
  mergeProductResetPayload,
  mergeUniqueIds,
  normalizeCollectionsOccasionIds,
  normalizeCollectionsSeasonIds,
  normalizeProductFormValues,
  normalizeProductPayload,
  normalizeProductServerErrors,
  normalizeSubscriptionDisclaimer,
  normalizeSubscriptionFrequencyValues,
  normalizeTagIds,
  shouldShowPresetVariantAction,
} from '@/lib/services/products/products.form'
import { subscriptionFrequencyOptions } from '@/lib/subscription-frequency'
import {
  useCreateProductMutation,
  useProductSearchTags,
  useRecommendProductSearchTagsMutation,
  useSoftDeleteProductMutation,
  useUpdateProductMutation,
} from '@/lib/services/products/products.query'
import {
  cn,
  formatCurrency,
  getErrorMessage,
  handleDragEnd,
  normalizeCurrencyValue,
  slugify,
  readProperty,
} from '@/lib/utils'
import {
  closestCenter,
  DndContext,
  MeasuringStrategy,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import { useStore } from '@tanstack/react-form'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  Camera,
  CalendarClock,
  ChevronDown,
  ExternalLink,
  FileText,
  Layers3,
  LibraryBig,
  Plus,
  Search,
  Tags,
  WandSparkles,
  X,
} from 'lucide-react'
import { Suspense, useDeferredValue, useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import AddOnSetPicker from '../products/AddOnSetPicker'
import CreateActionButton from '@/components/patterns/create-action-button'
import { DetailWell } from '@/components/patterns/detail-page'
import { EmptyState } from '@/components/patterns/empty-state'
import { MoneyInputControl } from '../form/MoneyInput'
import ProductCollectionPicker from '../products/ProductCollectionPicker'
import ProductSearchTagPicker from '../products/ProductSearchTagPicker'
import ImageLightboxGallery from '@/components/media/ImageLightboxGallery'
import { createLightboxPreviewItem } from '@/components/media/ImageLightboxPreviewTrigger'
import ImagePreview from '@/components/media/ImagePreview'
import ImageUploader from '@/components/media/ImageUploader'
import VariantTabTrigger from '@/components/products/VariantTabTrigger'
import {
  buildProductSeoPreviewSource,
  ProductSeoPreview,
} from '@/components/products/ProductSeoPreview'
import { Badge, BadgeAction, BadgeButton } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Dropzone } from '../ui/dropzone'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Checkbox } from '../ui/checkbox'
import { Label } from '../ui/label'
import { ScrollArea } from '../ui/scroll-area'
import { Switch } from '../ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { Typography } from '@/components/ui/typography'
import VariantRow from '@/components/products/VariantRow'
import SaveBar from './SaveBar'

const productRoleDescriptions: Record<ProductRole, string> = {
  product:
    'This item appears on your website in collections and occasion pages. Shoppers can buy it on its own.',
  standalone_add_on:
    'This item appears on your website and can also be offered as an optional add-on.',
  add_on_only:
    'This item is only offered as an add-on. It is hidden from website collections and occasion pages.',
  subscription_package:
    'This is a subscription package. Customers choose a tier and frequency, and you can feature it in product collections like any other product.',
}

const productRoleOptions: { id: ProductRole; name: string }[] = [
  { id: 'product', name: 'Product' },
  { id: 'standalone_add_on', name: 'Product or add-on' },
  { id: 'add_on_only', name: 'Add-on only' },
  { id: 'subscription_package', name: 'Subscription package' },
]

const productRoleRadioClassNames = {
  button: 'min-w-[8rem] flex-none justify-center text-center',
}

function SubscriptionFrequencyOptionsSection<
  TOnMount extends ProductEditorSyncValidator,
  TOnChange extends ProductEditorSyncValidator,
  TOnChangeAsync extends ProductEditorAsyncValidator,
  TOnBlur extends ProductEditorSyncValidator,
  TOnBlurAsync extends ProductEditorAsyncValidator,
  TOnSubmit extends ProductEditorSyncValidator,
  TOnSubmitAsync extends ProductEditorAsyncValidator,
  TOnDynamic extends ProductEditorSyncValidator,
  TOnDynamicAsync extends ProductEditorAsyncValidator,
  TOnServer extends ProductEditorAsyncValidator,
  TSubmitMeta,
>({
  form,
  id,
}: {
  form: ProductEditorForm<
    TOnMount,
    TOnChange,
    TOnChangeAsync,
    TOnBlur,
    TOnBlurAsync,
    TOnSubmit,
    TOnSubmitAsync,
    TOnDynamic,
    TOnDynamicAsync,
    TOnServer,
    TSubmitMeta
  >
  id: string
}) {
  const { isReadOnly } = useIsReadOnly()
  const selectedFrequencies = useStore(
    form.store,
    (state) => state.values.subscription_frequency_options ?? []
  )
  const selectedSet = new Set(selectedFrequencies)

  const toggleFrequency = (frequency: SubscriptionFrequency, checked: boolean) => {
    const nextValues = checked
      ? [...selectedSet, frequency]
      : selectedFrequencies.filter((value) => value !== frequency)

    form.setFieldValue(
      'subscription_frequency_options',
      normalizeSubscriptionFrequencyValues(nextValues)
    )
    void form.validateField('subscription_frequency_options', 'change')
  }

  return (
    <section className="flex flex-col gap-4 border-t border-border/60 pt-5">
      <div className="flex min-w-0 gap-3">
        <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-interaction dark:bg-primary/15">
          <CalendarClock className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 space-y-1">
          <Typography variant="cardTitle" className="block pb-0">
            Subscription Frequencies
          </Typography>
          <Typography variant="supporting" className="block max-w-3xl">
            Choose how often customers can receive this package.
          </Typography>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {subscriptionFrequencyOptions.map((option) => {
          const checkboxId = `${id}-subscription-frequency-${option.id}`
          const isChecked = selectedSet.has(option.id)

          return (
            <Label key={option.id} htmlFor={checkboxId} appearance="choiceCard">
              <Checkbox
                id={checkboxId}
                checked={isChecked}
                disabled={isReadOnly}
                onCheckedChange={(checked) => toggleFrequency(option.id, Boolean(checked))}
              />
              <span className="min-w-0 truncate">{option.name}</span>
            </Label>
          )
        })}
      </div>

      {selectedFrequencies.length === 0 ? (
        <Typography variant="label" tone="destructive">
          Choose at least one frequency.
        </Typography>
      ) : null}
    </section>
  )
}

function SubscriptionDisclaimerSection<
  TOnMount extends ProductEditorSyncValidator,
  TOnChange extends ProductEditorSyncValidator,
  TOnChangeAsync extends ProductEditorAsyncValidator,
  TOnBlur extends ProductEditorSyncValidator,
  TOnBlurAsync extends ProductEditorAsyncValidator,
  TOnSubmit extends ProductEditorSyncValidator,
  TOnSubmitAsync extends ProductEditorAsyncValidator,
  TOnDynamic extends ProductEditorSyncValidator,
  TOnDynamicAsync extends ProductEditorAsyncValidator,
  TOnServer extends ProductEditorAsyncValidator,
  TSubmitMeta,
>({
  form,
  id,
}: {
  form: ProductEditorForm<
    TOnMount,
    TOnChange,
    TOnChangeAsync,
    TOnBlur,
    TOnBlurAsync,
    TOnSubmit,
    TOnSubmitAsync,
    TOnDynamic,
    TOnDynamicAsync,
    TOnServer,
    TSubmitMeta
  >
  id: string
}) {
  return (
    <section className="flex flex-col gap-4 border-t border-border/60 pt-5">
      <div className="flex min-w-0 gap-3">
        <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-interaction dark:bg-primary/15">
          <FileText className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 space-y-1">
          <Typography variant="cardTitle" className="block pb-0">
            Subscription Disclaimer
          </Typography>
          <Typography variant="supporting" className="block max-w-3xl">
            This copy appears at the bottom of this subscription package page.
          </Typography>
        </div>
      </div>

      <form.AppField name="subscription_disclaimer_html">
        {(field) => (
          <field.RichTextInput
            id={`${id}-subscription-disclaimer`}
            label="Disclaimer"
            plainTextFieldName="subscription_disclaimer"
            placeholder={defaultSubscriptionDisclaimer}
          />
        )}
      </form.AppField>
    </section>
  )
}

function getAddOnSetCollections(set: ProductEditorAddOnSet | null | undefined) {
  return Array.isArray(set?.applies_to_collections) ? set.applies_to_collections : []
}

function getAddOnSetItems(set: ProductEditorAddOnSet | null | undefined) {
  return Array.isArray(set?.add_ons) ? set.add_ons : []
}

type MediaUrlInput = Pick<MediaFile, 'url' | 'path'> | MediaUrlInput[] | null | undefined

function getMediaUrl(media: MediaUrlInput): string | null {
  if (Array.isArray(media)) {
    return getMediaUrl(media[0])
  }

  return media?.url ?? media?.path ?? null
}

function getAddOnThumbnailUrl(addOn: NonNullable<ProductEditorAddOnSet['add_ons']>[number]) {
  return getMediaUrl(addOn?.media) ?? getMediaUrl(addOn?.defaultVariant?.media)
}

function AddOnSetPanelTab({
  count,
  Icon,
  label,
  value,
}: {
  count: number
  Icon: LucideIcon
  label: string
  value: string
}) {
  return (
    <TabsTrigger value={value}>
      <Icon aria-hidden="true" />
      {count} {label}
      {count === 1 ? '' : 's'}
    </TabsTrigger>
  )
}

function AddOnSetBadgeDropdown({
  path,
  set,
}: {
  path: ReturnType<typeof usePathHelper>
  set: ProductEditorAddOnSet
}) {
  const assignedCollections = getAddOnSetCollections(set)
  const addOns = getAddOnSetItems(set)
  const addOnCount = Number(set?.add_ons_count ?? addOns.length ?? 0)
  const collectionCount = assignedCollections.length
  const [activePanel, setActivePanel] = useState('collections')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <BadgeButton size="comfortable" wrap className="pr-1.5">
          <span className="min-w-0 truncate">{set.name}</span>
          <ChevronDown className="size-3.5 shrink-0" aria-hidden="true" />
        </BadgeButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" size="2xl" padding="none">
        <div className="space-y-3 p-4">
          <div className="min-w-0 space-y-2">
            <Typography variant="label" className="block truncate">
              {set.name}
            </Typography>
            <Tabs value={activePanel} onValueChange={setActivePanel} className="gap-0">
              <TabsList aria-label={`${set.name} details`}>
                <AddOnSetPanelTab
                  count={collectionCount}
                  Icon={LibraryBig}
                  label="collection"
                  value="collections"
                />
                <AddOnSetPanelTab count={addOnCount} Icon={Tags} label="add-on" value="addOns" />
              </TabsList>
            </Tabs>
          </div>

          {activePanel === 'collections' && assignedCollections.length > 0 ? (
            <ScrollArea
              className="h-fit max-h-48 rounded-xl border bg-muted/10"
              viewportClassName="max-h-48"
            >
              <ul className="space-y-1 p-2">
                {assignedCollections.map((collection) => (
                  <li
                    key={collection.id}
                    className="flex min-w-0 items-center gap-3 rounded-lg px-2 py-1.5 text-sm"
                  >
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border bg-background text-muted-foreground">
                      <LibraryBig className="size-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 truncate">{collection.name}</span>
                  </li>
                ))}
              </ul>
            </ScrollArea>
          ) : null}

          {activePanel === 'collections' && assignedCollections.length === 0 ? (
            <EmptyState
              size="inline"
              title="Assign to a product collection to make this add-on available."
            />
          ) : null}

          {activePanel === 'addOns' && addOns.length > 0 ? (
            <ScrollArea
              className="h-fit max-h-48 rounded-xl border bg-muted/10"
              viewportClassName="max-h-48"
            >
              <ul className="space-y-1 p-2">
                {addOns.map((addOn) => {
                  const thumbnailUrl = getAddOnThumbnailUrl(addOn)

                  return (
                    <li
                      key={addOn.id}
                      className="flex min-w-0 items-center gap-3 rounded-lg px-2 py-1.5 text-sm"
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-background">
                        {thumbnailUrl ? (
                          <img
                            src={thumbnailUrl}
                            alt={`${addOn.name} thumbnail`}
                            className="size-full object-cover"
                          />
                        ) : (
                          <Camera className="size-4 text-muted-foreground" aria-hidden="true" />
                        )}
                      </span>
                      <span className="min-w-0 truncate">{addOn.name}</span>
                    </li>
                  )
                })}
              </ul>
            </ScrollArea>
          ) : null}

          {activePanel === 'addOns' && addOns.length === 0 ? (
            <EmptyState size="inline" title="No add-ons are in this collection yet." />
          ) : null}

          <Button asChild variant="outline" size="sm" className="w-full justify-center">
            <a href={path.addOnSets.show(set.id).href} target="_blank" rel="noreferrer">
              <ExternalLink className="size-4" />
              View/edit Add-on Collection
            </a>
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const classificationSubcardClass =
  'rounded-xl border border-border/60 bg-background/45 px-5 shadow-xs dark:bg-background/25'

function ClassificationEmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="py-1">
      <Badge variant="placeholder" size="placeholder">
        {children}
      </Badge>
    </div>
  )
}

function ClassificationRow({
  action = null,
  children,
  className = '',
  description,
  icon: Icon,
  id,
  title,
}: {
  action?: ReactNode
  children?: ReactNode
  className?: string
  description?: ReactNode
  icon?: LucideIcon
  id?: string
  title: ReactNode
}) {
  return (
    <section id={id} className={cn('@container scroll-mt-4 py-6', className)}>
      <div className="flex flex-col gap-3 @lg:flex-row @lg:items-start @lg:justify-between">
        <div className="flex min-w-0 gap-4">
          {Icon ? (
            <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-interaction dark:bg-primary/15">
              <Icon className="size-4" aria-hidden="true" />
            </span>
          ) : null}
          <div className="min-w-0 space-y-2 pt-0.5">
            <div className="space-y-1">
              <Typography variant="cardTitle" className="block pb-0">
                {title}
              </Typography>
              {description ? (
                <Typography variant="supporting" className="block max-w-3xl">
                  {description}
                </Typography>
              ) : null}
            </div>
          </div>
        </div>

        {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
      </div>

      {children ? <div className="mt-5 @lg:pl-[52px]">{children}</div> : null}
    </section>
  )
}

type SavedProductFormSource = ProductFormSource & Pick<Product, 'id'>
type SavedProductFormValues = ProductFormValues & Pick<Product, 'id'>

interface ProductFormProps {
  initialData?: SavedProductFormSource | null
  locations?: Parameters<typeof canTrackInventoryForLocations>[0]
  organizationId?: OrganizationIdInput
  activePriceAdjustments?: ProductPriceAdjustment[]
  historyLink?: AppLinkProps | null
  subscriptionCollection?: Collection | null
}
type ProductFormInnerProps = ProductFormProps & {
  onSavedProduct?: ((product: SavedProductFormValues) => void) | null
}

const ProductFormInner = ({
  initialData,
  locations = [],
  organizationId = undefined,
  activePriceAdjustments = [],
  historyLink = null,
  onSavedProduct = null,
  subscriptionCollection: providedSubscriptionCollection = null,
}: ProductFormInnerProps) => {
  const id = useId()
  const path = usePathHelper()
  const navigate = useNavigate()
  const ability = useAbility()
  const { isReadOnly } = useIsReadOnly()
  const { organization: activeOrganization } = useOrganizationContext()
  const summarySectionIds = {
    photos: `${id}-photos-section`,
    collections: `${id}-collections-section`,
    variants: `${id}-variants-section`,
    appliesTo: `${id}-add-on-applies-to-section`,
  }
  const [isMediaUploaderOpen, setIsMediaUploaderOpen] = useState(false)
  const [isCollectionsDialogOpen, setIsCollectionsDialogOpen] = useState(false)
  const [isAddOnSetsDialogOpen, setIsAddOnSetsDialogOpen] = useState(false)
  const [isPresetDialogOpen, setIsPresetDialogOpen] = useState(false)
  const [presetStandardPrice, setPresetStandardPrice] = useState('')
  const deferredPresetStandardPrice = useDeferredValue(presetStandardPrice)
  const [presetStandardPriceError, setPresetStandardPriceError] = useState('')
  const [seoPreviewEditorSessionKey, setSeoPreviewEditorSessionKey] = useState(0)
  const createProductMutation = useCreateProductMutation()
  const updateProductMutation = useUpdateProductMutation()
  const recommendSearchTagsMutation = useRecommendProductSearchTagsMutation()
  const deleteProductMutation = useSoftDeleteProductMutation({
    cleanupOnSuccess: false,
  })
  const { data: merchandisingTagGroups = [] } = useMerchandisingTagGroups({ organizationId })
  const { data: searchTagGroups = [] } = useProductSearchTags({
    organizationId,
  })
  const merchandisingTagOptions = flattenMerchandisingTagGroups(merchandisingTagGroups)
  const productSearchSeasonIds = searchTagGroups
    .filter((group) => group.tag_type === 'season')
    .flatMap((group) => group.tags ?? [])
    .map((tag) => Number(tag.id))
  const assignedSeasonIds = Object.values(initialData?.search_tags?.season ?? {}).map((tag) =>
    Number(tag.id)
  )
  const seasonIdSet = new Set(
    merchandisingTagOptions
      .filter((tag) => tag.tag_type === 'season')
      .map((tag) => Number(tag.id))
      .concat(productSearchSeasonIds, assignedSeasonIds)
  )
  const customerSearchTagGroups = searchTagGroups.filter((group) => group.tag_type !== 'season')
  const liveVariants = initialData?.variants ?? []
  const seoOrganization = initialData?.organization ?? activeOrganization ?? null
  const mediaSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    })
  )
  const [createDefaultValues] = useState(() => ({
    ...emptyProduct,
    variants: [createEmptyVariant({ isDefault: true })],
  }))
  const defaultValues: ProductEditorValues = initialData
    ? normalizeProductFormValues(initialData)
    : createDefaultValues
  const subscriptionCollection =
    findSubscriptionPackageCollection(initialData?.collections) ?? providedSubscriptionCollection
  const [activeVariantId, setActiveVariantId] = useState(() =>
    getPreferredActiveVariantId(defaultValues.variants)
  )
  const showSideSummary = initialData
  const canTrackInventory = canTrackInventoryForLocations(locations)
  const canViewCollections = ability.can('view', 'collections')
  const deferredPresetPriceValue = deferredPresetStandardPrice
    ? normalizeCurrencyValue(deferredPresetStandardPrice)
    : 0
  const [collectionsWereEdited, setCollectionsWereEdited] = useState(false)
  const urlSlugManuallyEditedRef = useRef(false)
  const productRoleTransitionMemoryRef = useRef(createProductRoleTransitionMemory(defaultValues))
  const rememberStandaloneSearchTags = (tagIds: number[]) => {
    const normalizedTagIds = normalizeTagIds(tagIds)
    productRoleTransitionMemoryRef.current.searchTagIds = normalizedTagIds

    return normalizedTagIds
  }
  const rememberStandaloneOccasions = (occasionIds: number[]) => {
    const normalizedOccasionIds = normalizeTagIds(occasionIds)
    productRoleTransitionMemoryRef.current.occasionIds = normalizedOccasionIds

    return normalizedOccasionIds
  }
  const rememberStandaloneCollections = (
    collections: ProductEditorCollection[] | null | undefined,
    wasEdited = true
  ) => {
    const nextCollections = collections ?? []
    productRoleTransitionMemoryRef.current.collections = nextCollections
    productRoleTransitionMemoryRef.current.collectionsWereEdited = wasEdited

    return nextCollections
  }
  const resetSeoPreviewEditor = () =>
    setSeoPreviewEditorSessionKey((currentSessionKey) => currentSessionKey + 1)
  const scrollToFormSection = (sectionName: keyof typeof summarySectionIds) => {
    const targetId = summarySectionIds[sectionName]

    if (!targetId) {
      return
    }

    document.getElementById(targetId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
  const handleNameBlur = (value: string) => {
    const normalizedName = String(value ?? '').trim()
    const currentSlug = String(form.getFieldValue('url_slug') ?? '').trim()
    const shouldSyncGeneratedSlug =
      !initialData && normalizedName && !urlSlugManuallyEditedRef.current
    const nextSlug = shouldSyncGeneratedSlug
      ? slugify(normalizedName)
      : normalizedName && !currentSlug
        ? slugify(normalizedName)
        : currentSlug

    if (shouldSyncGeneratedSlug || (normalizedName && !currentSlug)) {
      form.setFieldValue('url_slug', nextSlug)
    }
  }
  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmitAsync: async ({ value }) => {
        const normalizedValue = normalizeProductPayload(value, {
          collectionsWereEdited,
        })
        const submittedItemLabel = getProductRoleLabel(getProductRole(value))

        try {
          if (initialData) {
            const updatedProduct = await updateProductMutation.mutateAsync({
              ...normalizedValue,
              productId: initialData.id,
            })
            const resetProduct = normalizeProductFormValues(
              mergeProductResetPayload(updatedProduct, value)
            )

            toast.success(`${submittedItemLabel} updated successfully`)
            setCollectionsWereEdited(false)
            resetSeoPreviewEditor()
            onSavedProduct?.(resetProduct)
          } else {
            await createProductMutation.mutateAsync(normalizedValue, {
              onSuccess: (response) => {
                toast.success(`${submittedItemLabel} created successfully`)
                setCollectionsWereEdited(false)
                if (response?.product_id) {
                  void navigate({
                    ...path.products.show(response.product_id).link,
                    ignoreBlocker: true,
                  })
                }
              },
            })
          }
        } catch (error) {
          const serverErrors = readProperty(
            readProperty(readProperty(error, 'response'), 'data'),
            'errors'
          )
          const normalizedServerErrors = normalizeProductServerErrors(serverErrors)
          const firstInvalidVariantIndex = getFirstInvalidVariantIndex(normalizedServerErrors)

          if (firstInvalidVariantIndex !== null) {
            const invalidVariant = value?.variants?.[firstInvalidVariantIndex]

            if (invalidVariant?.id) {
              setActiveVariantId(String(invalidVariant.id))
            }
          }

          const action = initialData ? 'update' : 'creation'
          toast.error(`${submittedItemLabel} ${action} failed`, {
            description: getProductValidationToastDescription(error, value),
          })
          if (serverErrors) {
            return { fields: normalizedServerErrors }
          }
        }
        return undefined
      },
    },
  })
  const productRole = useStore(form.store, (state) => getProductRole(state.values))
  const selectedProductOccasionIds = useStore(
    form.store,
    (state) => state.values.occasion_ids ?? []
  )
  const selectedProductSearchTagIds = useStore(
    form.store,
    (state) => state.values.search_tag_ids ?? []
  )
  const selectedSeasonIds = selectedProductSearchTagIds.filter((tagId) =>
    seasonIdSet.has(Number(tagId))
  )
  const selectedMerchandisingTagIds = mergeUniqueIds(selectedProductOccasionIds, selectedSeasonIds)
  const selectedCustomerSearchTagIds = selectedProductSearchTagIds.filter(
    (tagId) => !seasonIdSet.has(Number(tagId))
  )
  const seoPreviewSource = useStore(form.store, (state) =>
    buildProductSeoPreviewSource(state.values, canViewCollections, subscriptionCollection)
  )
  const isSubscriptionPackage = isSubscriptionPackageRole(productRole)
  const selectedCollections = useStore(form.store, (state) => state.values.collections ?? [])
  const draftActivePriceAdjustment = isSubscriptionPackage
    ? null
    : getDraftProductActivePriceAdjustment({
        collections: selectedCollections,
        priceAdjustments: activePriceAdjustments,
      })
  const isAddOnProduct = isAddOnProductRole(productRole)
  const isAddOnOnlyProduct = productRole === 'add_on_only'
  const isSoldStandalone = !isAddOnOnlyProduct && !isSubscriptionPackage
  const handleProductRoleChange = (rawNextRole: unknown, rawPreviousRole: unknown) => {
    const nextRole = productRoleOptions.find((option) => option.id === rawNextRole)?.id
    const previousRole = productRoleOptions.find((option) => option.id === rawPreviousRole)?.id
    if (!nextRole || !previousRole) return

    const transition = transitionProductRole({
      values: {
        ...form.store.state.values,
        subscription_disclaimer_default_html: plainTextToRichTextHtml(
          normalizeSubscriptionDisclaimer(form.getFieldValue('subscription_disclaimer'))
        ),
        subscription_disclaimer_html_is_empty: !normalizeRichTextHtml(
          form.getFieldValue('subscription_disclaimer_html')
        ),
      },
      previousRole,
      nextRole,
      memory: productRoleTransitionMemoryRef.current,
      collectionsWereEdited,
    })

    const hasPatchField = (fieldName: string): fieldName is keyof typeof transition.patch =>
      Object.hasOwn(transition.patch, fieldName)
    Object.entries(transition.patch).forEach(([fieldName]) => {
      if (hasPatchField(fieldName)) {
        const value = transition.patch[fieldName]
        if (value !== undefined) form.setFieldValue(fieldName, value)
      }
    })
    productRoleTransitionMemoryRef.current = transition.memory
    setCollectionsWereEdited(transition.collectionsWereEdited)
  }
  const showPresetVariantShortcut = useStore(form.store, (state) =>
    shouldShowPresetVariantAction(state.values.variants ?? [])
  )
  useStore(form.store, (state) => getVariantWorkspaceSignature(state.values.variants ?? []))

  const suggestSearchTags = async () => {
    try {
      const recommendedProduct = await recommendSearchTagsMutation.mutateAsync({
        draft: buildProductSearchTagRecommendationDraft(form.store.state.values),
        includeSoftTags: true,
      })
      const suggestedTagIds = normalizeTagIds(
        recommendedProduct?.recommendation?.tags?.map((tag) => tag.id)
      )

      if (suggestedTagIds.length === 0) {
        toast.success('No suggested details found')

        return null
      }

      return suggestedTagIds
    } catch (error) {
      toast.error('Suggested details failed', {
        description: getErrorMessage(error),
      })

      return null
    }
  }

  const applySuggestedSearchTags = async (
    field: { setValue: (ids: number[]) => void },
    nextTagIds: number[]
  ) => {
    field.setValue(rememberStandaloneSearchTags(nextTagIds))
    toast.success('Suggested details applied')
  }

  const updateProductMerchandisingTags = (nextTagIds: number[]) => {
    const { occasionIds, seasonIds } = splitMerchandisingTagIds(
      nextTagIds,
      merchandisingTagOptions,
      {
        occasionIds: selectedProductOccasionIds,
        seasonIds: selectedSeasonIds,
      }
    )
    const nextSearchTagIds = [
      ...selectedProductSearchTagIds.filter((tagId) => !seasonIdSet.has(Number(tagId))),
      ...seasonIds,
    ]

    form.setFieldValue('occasion_ids', rememberStandaloneOccasions(occasionIds))
    form.setFieldValue('search_tag_ids', rememberStandaloneSearchTags(nextSearchTagIds))
    void form.validateField('occasion_ids', 'change')
    void form.validateField('search_tag_ids', 'change')
  }

  const handleResetForm = (e?: MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    setCollectionsWereEdited(false)
    productRoleTransitionMemoryRef.current = createProductRoleTransitionMemory(defaultValues)
    urlSlugManuallyEditedRef.current = false
    resetSeoPreviewEditor()
    form.reset()
    setActiveVariantId(getPreferredActiveVariantId(defaultValues.variants))
    void form.validateField('variants', 'mount')
  }

  const addBlankVariant = () => {
    const currentVariants = form.getFieldValue('variants') ?? []
    const nextVariant = createEmptyVariant({
      isDefault: currentVariants.length === 0,
    })

    form.pushFieldValue('variants', nextVariant)
    setActiveVariantId(String(nextVariant.id))
  }

  const addCopiedVariant = (sourceVariant: ProductEditorVariant) => {
    const currentVariants = form.getFieldValue('variants') ?? []
    const nextVariant = createCopiedVariant(sourceVariant, {
      isDefault: currentVariants.length === 0,
    })

    form.pushFieldValue('variants', nextVariant)
    setActiveVariantId(String(nextVariant.id))
  }

  const addCopiedVariantByIndex = (sourceVariantIndex: number) => {
    const sourceVariant = (form.getFieldValue('variants') ?? [])[sourceVariantIndex]

    if (!sourceVariant) {
      return
    }

    addCopiedVariant(sourceVariant)
  }

  const applyPresetVariants = () => {
    if (!presetStandardPrice.trim()) {
      setPresetStandardPriceError('Enter a starting Standard price first.')
      return
    }

    setPresetStandardPriceError('')
    const nextVariants = createStandardDeluxePremiumVariants(
      normalizeCurrencyValue(presetStandardPrice)
    )

    form.setFieldValue('variants', nextVariants)
    setActiveVariantId(getPreferredActiveVariantId(nextVariants))
    setIsPresetDialogOpen(false)
    setPresetStandardPrice('')
  }

  const handleVariantDragEnd = ({ active, over }: Pick<DragEndEvent, 'active' | 'over'>) => {
    if (active?.id === over?.id) {
      return
    }

    const currentVariants = form.getFieldValue('variants') ?? []
    const oldIndex = currentVariants.findIndex((variant) => variant?.id === active?.id)
    const newIndex = currentVariants.findIndex((variant) => variant?.id === over?.id)

    if (oldIndex < 0 || newIndex < 0) {
      return
    }

    form.setFieldValue('variants', arrayMove(currentVariants, oldIndex, newIndex))
  }

  const variants = form.getFieldValue('variants') ?? []
  const copyableVariants = variants
    .map((variant, index) => ({
      id: String(variant?.id ?? index),
      index,
      label: getVariantLabel(variant, index),
    }))
    .filter(({ index }) => !isBlankVariant(variants[index]))
  const resolvedActiveVariantId = variants.some(
    (variant) => String(variant?.id) === String(activeVariantId)
  )
    ? activeVariantId
    : getPreferredActiveVariantId(variants)
  const activeVariantIndex = variants.findIndex(
    (variant) => String(variant?.id) === String(resolvedActiveVariantId)
  )
  const activeVariant = activeVariantIndex >= 0 ? variants[activeVariantIndex] : null

  return (
    <div className="w-full max-w-none">
      <form.AppForm>
        <form.Form>
          <div className="grid w-full items-start gap-6 xl:grid-cols-3">
            {showSideSummary ? (
              <aside
                className={
                  initialData
                    ? 'min-w-0 xl:sticky xl:top-4 xl:col-start-3 xl:row-start-1 xl:self-start'
                    : 'min-w-0 xl:col-start-3 xl:row-start-1 xl:self-start'
                }
              >
                <ProductSummarySidebar
                  canViewCollections={canViewCollections}
                  form={form}
                  id={id}
                  initialCollections={initialData?.collections ?? []}
                  isAddOnProduct={isAddOnProduct}
                  isSoldStandalone={isSoldStandalone}
                  isSubscriptionPackage={isSubscriptionPackage}
                  onJumpToSection={scrollToFormSection}
                  previewSource={seoPreviewSource}
                  productRole={productRole}
                  sectionIds={summarySectionIds}
                />
              </aside>
            ) : null}

            <div className="grid min-w-0 gap-6 xl:col-span-2 xl:col-start-1 xl:row-start-1">
              <Card>
                <CardHeader>
                  <div
                    className={
                      showSideSummary
                        ? 'flex items-start justify-between gap-4'
                        : 'flex flex-col gap-4 md:flex-row md:items-start md:justify-between'
                    }
                  >
                    <div className="min-w-0">
                      <CardTitle>Product</CardTitle>
                      <CardDescription>
                        Start by choosing how customers can buy this item.
                      </CardDescription>
                    </div>

                    {showSideSummary ? (
                      <ProductMoreActionsMenu
                        deleteProductMutation={deleteProductMutation}
                        historyLink={historyLink}
                        isAddOnProduct={isAddOnProduct}
                        isReadOnly={isReadOnly}
                        product={initialData}
                        productRole={productRole}
                      />
                    ) : (
                      <ProductVisibilityPicker
                        className="w-full md:w-[22rem] md:max-w-full md:shrink-0"
                        form={form}
                        id={`${id}-storefront-visibility`}
                        productRole={productRole}
                      />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <form.AppField name="product_role">
                    {(field) => (
                      <div className="space-y-2">
                        <field.RadioButtonGroup
                          appearance="segmented"
                          id={`${id}-product-role`}
                          label="What is this item?"
                          classNames={productRoleRadioClassNames}
                          options={productRoleOptions}
                          onValueChange={handleProductRoleChange}
                          size="sm"
                          unselectedVariant="outline"
                        />
                        <Typography variant="supporting" className="block">
                          {productRoleDescriptions[field.state.value] ??
                            productRoleDescriptions.product}
                        </Typography>
                      </div>
                    )}
                  </form.AppField>

                  <form.AppField name="name">
                    {(field) => (
                      <field.TextInput
                        id={`${id}-name`}
                        label="Name"
                        onBlur={() => handleNameBlur(field.state.value)}
                      />
                    )}
                  </form.AppField>

                  <form.AppField name="description_html">
                    {(field) => (
                      <field.RichTextInput
                        id={`${id}-description`}
                        label="Description"
                        plainTextFieldName="description"
                        placeholder="Describe the flowers, colors, and anything customers should know."
                      />
                    )}
                  </form.AppField>

                  {isSubscriptionPackage ? (
                    <>
                      <SubscriptionFrequencyOptionsSection form={form} id={id} />
                      <SubscriptionDisclaimerSection form={form} id={id} />
                    </>
                  ) : null}

                  {!isAddOnOnlyProduct ? (
                    <form.AppField name="is_designers_choice">
                      {(field) => (
                        <div className="flex flex-col gap-4 border-t border-border/60 pt-5 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex min-w-0 gap-3">
                            <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-interaction dark:bg-primary/15">
                              <WandSparkles className="size-4" aria-hidden="true" />
                            </span>
                            <div className="min-w-0 space-y-1">
                              <Typography variant="cardTitle" className="block pb-0">
                                Designer&apos;s Choice
                              </Typography>
                              <Typography variant="supporting" className="block max-w-3xl">
                                Use this when the florist will design the final arrangement from the
                                selected price point.
                              </Typography>
                            </div>
                          </div>
                          <Label
                            htmlFor={`${id}-is-designers-choice`}
                            appearance="choicePill"
                            tone="muted"
                          >
                            <span>{field.state.value ? 'On' : 'Off'}</span>
                            <Switch
                              id={`${id}-is-designers-choice`}
                              name={field.name}
                              checked={Boolean(field.state.value)}
                              disabled={isReadOnly}
                              aria-label="Designer's Choice"
                              onCheckedChange={(checked) => field.handleChange(Boolean(checked))}
                            />
                          </Label>
                        </div>
                      )}
                    </form.AppField>
                  ) : null}
                </CardContent>
              </Card>

              <div className="grid min-w-0 gap-5 xl:grid-cols-2">
                <section
                  id={summarySectionIds.photos}
                  className="min-w-0 scroll-mt-4 space-y-5 rounded-xl border border-border bg-card p-6 xl:col-span-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="inline-flex items-center gap-2">
                        <Camera className="size-5" />
                        <Typography variant="h4" className="block">
                          Photos
                        </Typography>
                      </div>
                      <Typography variant="supporting" className="block">
                        Add the photos customers will see. Variants can use these below.
                      </Typography>
                    </div>
                  </div>

                  <form.AppField name="media">
                    {(field) => {
                      const uploadedMedia = field.state.value ?? []
                      const getCurrentUploadedMediaIds = () =>
                        new Set(
                          (form.getFieldValue('media') ?? [])
                            .map((media) => getMediaId(media))
                            .filter(Boolean)
                        )
                      const previewItems: LightboxItem[] = []
                      const previewIndexes = uploadedMedia.map((media, index) => {
                        const previewItem = createLightboxPreviewItem({
                          id: `product-media-${media?.id ?? index}`,
                          media,
                          alt: media?.name || `Uploaded image ${index + 1}`,
                          caption: media?.name || `Uploaded image ${index + 1}`,
                        })

                        if (!previewItem) {
                          return null
                        }

                        const previewIndex = previewItems.length

                        previewItems.push(previewItem)

                        return previewIndex
                      })

                      return (
                        <>
                          <ul className="flex flex-wrap items-start gap-4">
                            <DndContext
                              sensors={mediaSensors}
                              collisionDetection={closestCenter}
                              onDragEnd={handleDragEnd(field)}
                              measuring={{
                                droppable: {
                                  strategy: MeasuringStrategy.Always,
                                },
                              }}
                            >
                              <SortableContext
                                items={uploadedMedia.flatMap((media) =>
                                  typeof media.id === 'string' || typeof media.id === 'number'
                                    ? [media.id]
                                    : []
                                )}
                              >
                                <ImageLightboxGallery items={previewItems} className="contents">
                                  {({ getTriggerProps }) =>
                                    uploadedMedia.map((media, index) => (
                                      <ImagePreview
                                        key={media.id}
                                        media={media}
                                        index={index}
                                        lightboxTriggerProps={
                                          previewIndexes[index] == null
                                            ? undefined
                                            : getTriggerProps(previewIndexes[index], {
                                                mode: 'button',
                                              })
                                        }
                                      />
                                    ))
                                  }
                                </ImageLightboxGallery>
                              </SortableContext>
                            </DndContext>
                            {!isReadOnly ? (
                              <li className="inline-flex items-center">
                                <Dropzone asChild>
                                  <button
                                    id={`${id}-add-media`}
                                    type="button"
                                    data-raw-button-owner="dropzone-trigger"
                                    className="group flex aspect-square w-40 flex-shrink-0 cursor-pointer flex-col items-center justify-center gap-2 p-4 text-center text-muted-foreground hover:text-foreground"
                                    onClick={() => setIsMediaUploaderOpen(true)}
                                  >
                                    <span className="flex size-10 items-center justify-center rounded-full bg-accentSecondary text-interaction transition-colors group-hover:bg-accentSecondary/70">
                                      <Plus className="size-4" aria-hidden="true" />
                                    </span>
                                    <span className="block text-sm font-semibold text-foreground">
                                      {uploadedMedia.length > 0 ? 'Add another photo' : 'Add photo'}
                                    </span>
                                  </button>
                                </Dropzone>
                              </li>
                            ) : null}
                          </ul>
                          {!isReadOnly ? (
                            <ImageUploader
                              open={isMediaUploaderOpen}
                              defaultUploadDestination={
                                PhotoManagerDefaultUploadDestination.ProductPhotos
                              }
                              selectionMode={PhotoManagerSelectionMode.Multiple}
                              onRequestClose={() => setIsMediaUploaderOpen(false)}
                              onUploadSuccess={(_, response) => {
                                const media = response?.body
                                const mediaId = getMediaId(media)

                                if (
                                  media?.url &&
                                  mediaId &&
                                  !getCurrentUploadedMediaIds().has(mediaId)
                                ) {
                                  field.pushValue(media)
                                }
                              }}
                              onSelectAssets={(assets = []) => {
                                const existingIds = getCurrentUploadedMediaIds()
                                const nextMedia = []
                                let skippedCount = 0

                                for (const asset of assets) {
                                  const media = getAssetMedia(asset)
                                  const mediaId = getMediaId(media)

                                  if (!media?.url || !mediaId || existingIds.has(mediaId)) {
                                    skippedCount++
                                    continue
                                  }

                                  existingIds.add(mediaId)
                                  nextMedia.push(media)
                                }

                                nextMedia.forEach((media) => field.pushValue(media))

                                return {
                                  insertedCount: nextMedia.length,
                                  skippedCount,
                                }
                              }}
                            />
                          ) : null}
                        </>
                      )
                    }}
                  </form.AppField>
                </section>

                <Card id={summarySectionIds.variants} className="min-w-0 scroll-mt-4 xl:col-span-2">
                  <CardHeader>
                    <CardTitle className="inline-flex items-center justify-between">
                      <div className="inline-flex items-center gap-2">
                        <Layers3 className="size-5" />
                        <span>{isSubscriptionPackage ? 'Tiers' : 'Variants'}</span>
                      </div>
                      {!isReadOnly && showPresetVariantShortcut ? (
                        <Button variant="outline" onClick={() => setIsPresetDialogOpen(true)}>
                          <WandSparkles className="size-4" />
                          Standard / Deluxe / Premium
                        </Button>
                      ) : null}
                    </CardTitle>
                    <CardDescription>
                      {isSubscriptionPackage
                        ? 'Set up the subscription tiers customers can choose.'
                        : isAddOnProduct
                          ? 'Set up the add-on options customers can choose.'
                          : 'Set up the choices customers can order, like Standard, Deluxe, or Premium.'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {variants.length > 0 ? (
                      <Tabs
                        value={resolvedActiveVariantId ?? undefined}
                        onValueChange={setActiveVariantId}
                        className="gap-4"
                        appearance="embedded"
                        size="dense"
                      >
                        <div className="flex items-center gap-2">
                          <DndContext
                            collisionDetection={closestCenter}
                            onDragEnd={handleVariantDragEnd}
                          >
                            <SortableContext
                              items={variants.map((variant) => variant.id)}
                              strategy={rectSortingStrategy}
                            >
                              <TabsList
                                aria-label={
                                  isSubscriptionPackage ? 'Subscription tiers' : 'Product variants'
                                }
                                layout="wrap"
                                className="flex-1 items-center gap-1.5"
                              >
                                {variants.map((variant, index) => (
                                  <VariantTabTrigger
                                    key={variant.id}
                                    sortableId={variant.id}
                                    value={String(variant.id)}
                                    label={getVariantLabel(variant, index)}
                                    isDefault={Boolean(variant?.is_default)}
                                    isActive={
                                      String(variant.id) === String(resolvedActiveVariantId)
                                    }
                                  />
                                ))}
                              </TabsList>
                            </SortableContext>
                          </DndContext>

                          {!isReadOnly ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="icon-xs"
                                  className="shrink-0"
                                  aria-label={isSubscriptionPackage ? 'Add Tier' : 'Add Variant'}
                                >
                                  <Plus className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" size="sm">
                                <DropdownMenuItem onSelect={addBlankVariant}>
                                  Blank
                                </DropdownMenuItem>

                                {copyableVariants.length > 0 ? (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuLabel>Copy From</DropdownMenuLabel>
                                    {copyableVariants.map((variant) => (
                                      <DropdownMenuItem
                                        key={`copy-${variant.id}`}
                                        onSelect={() => addCopiedVariantByIndex(variant.index)}
                                      >
                                        {variant.label}
                                      </DropdownMenuItem>
                                    ))}
                                  </>
                                ) : null}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </div>

                        {activeVariant ? (
                          <TabsContent value={resolvedActiveVariantId ?? ''} className="mt-0">
                            <VariantRow
                              form={form}
                              key={activeVariant.id}
                              id={activeVariant.id}
                              index={activeVariantIndex}
                              liveVariant={
                                liveVariants.find(
                                  (liveVariant) =>
                                    String(liveVariant?.id) === String(activeVariant?.id)
                                ) ?? null
                              }
                              draftActivePriceAdjustment={draftActivePriceAdjustment}
                              canTrackInventory={canTrackInventory}
                              isSubscriptionPackage={isSubscriptionPackage}
                              showMinimumOptionNotice={Boolean(initialData)}
                            />
                          </TabsContent>
                        ) : null}
                      </Tabs>
                    ) : (
                      <EmptyState
                        size="compact"
                        title={isSubscriptionPackage ? 'No tiers yet' : 'No variants yet'}
                        description={`Add your first ${isSubscriptionPackage ? 'tier' : 'variant'}, or start with Standard, Deluxe, and Premium.`}
                      />
                    )}
                  </CardContent>
                </Card>

                {isSoldStandalone || isSubscriptionPackage ? (
                  <Card className="min-w-0 scroll-mt-4 xl:col-span-2">
                    <CardHeader className="border-b border-border/60">
                      <div className="flex items-start gap-4">
                        <span className="mt-1 flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-interaction dark:bg-primary/15">
                          <Search className="size-5" aria-hidden="true" />
                        </span>
                        <div className="min-w-0 space-y-1">
                          <CardTitle className="block">
                            Help Customers Find This {isSubscriptionPackage ? 'Package' : 'Product'}
                          </CardTitle>
                          <Typography variant="supporting" className="block max-w-4xl">
                            {isSubscriptionPackage
                              ? 'Add searchable details for this subscription package.'
                              : 'Once the product details are in place, use this section to choose where it appears on the website and how customers can filter or search for it.'}
                          </Typography>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      {canViewCollections ? (
                        <Can do="view" on="collections">
                          <ClassificationRow
                            id={summarySectionIds.collections}
                            className={classificationSubcardClass}
                            icon={LibraryBig}
                            title="Collections"
                            description="Choose which pages on your website this product shows up on."
                            action={
                              !isReadOnly ? (
                                <Can do="edit" on="collections">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="shrink-0 px-3.5"
                                    onClick={() => setIsCollectionsDialogOpen(true)}
                                  >
                                    <LibraryBig className="size-4" />
                                    Choose Collections
                                  </Button>
                                </Can>
                              ) : null
                            }
                          >
                            <form.AppField name="collections">
                              {(field) => (
                                <div className="space-y-3">
                                  {field.state.value?.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                      {field.state.value.map((collection) => (
                                        <Badge
                                          key={collection.id}
                                          variant="botanical"
                                          size="choice"
                                        >
                                          <LibraryBig
                                            className="size-3.5 shrink-0 text-chart-3"
                                            aria-hidden="true"
                                          />
                                          <Link
                                            {...path.collections.show(collection.id).link}
                                            className="hover:underline hover:underline-offset-2"
                                          >
                                            {collection.name}
                                          </Link>
                                          {!isReadOnly ? (
                                            <BadgeAction
                                              className="-mr-1"
                                              aria-label={`Remove ${collection.name}`}
                                              onClick={() => {
                                                const nextCollections = (
                                                  field.state.value ?? []
                                                ).filter(
                                                  (selectedCollection) =>
                                                    String(selectedCollection?.id) !==
                                                    String(collection.id)
                                                )

                                                field.setValue(
                                                  rememberStandaloneCollections(nextCollections)
                                                )
                                                setCollectionsWereEdited(true)
                                                void form.validateField('collections', 'change')
                                              }}
                                            >
                                              <X className="size-3" aria-hidden="true" />
                                            </BadgeAction>
                                          ) : null}
                                        </Badge>
                                      ))}
                                    </div>
                                  ) : (
                                    <ClassificationEmptyState>
                                      No custom website pages selected yet.
                                    </ClassificationEmptyState>
                                  )}

                                  {!isReadOnly ? (
                                    <Can do="edit" on="collections">
                                      <Dialog
                                        open={isCollectionsDialogOpen}
                                        onOpenChange={setIsCollectionsDialogOpen}
                                      >
                                        <DialogContent size="large">
                                          <DialogHeader>
                                            <DialogTitle>Collections</DialogTitle>
                                            <DialogDescription>
                                              Choose which website pages should include this
                                              product.
                                            </DialogDescription>
                                          </DialogHeader>
                                          {isCollectionsDialogOpen ? (
                                            <ProductCollectionPicker
                                              organizationId={organizationId}
                                              initialData={field.state.value ?? []}
                                              onConfirm={(collections) => {
                                                const nextOccasionIds = mergeUniqueIds(
                                                  form.getFieldValue('occasion_ids'),
                                                  normalizeCollectionsOccasionIds(collections)
                                                )
                                                const nextSeasonIds = mergeUniqueIds(
                                                  selectedSeasonIds,
                                                  normalizeCollectionsSeasonIds(collections)
                                                )
                                                const nextSearchTagIds = [
                                                  ...(
                                                    form.getFieldValue('search_tag_ids') ?? []
                                                  ).filter(
                                                    (tagId) => !seasonIdSet.has(Number(tagId))
                                                  ),
                                                  ...nextSeasonIds,
                                                ]

                                                field.setValue(
                                                  rememberStandaloneCollections(collections)
                                                )
                                                if (
                                                  !areSameIdList(
                                                    form.getFieldValue('occasion_ids'),
                                                    nextOccasionIds
                                                  )
                                                ) {
                                                  rememberStandaloneOccasions(nextOccasionIds)
                                                  form.setFieldValue(
                                                    'occasion_ids',
                                                    nextOccasionIds
                                                  )
                                                  void form.validateField('occasion_ids', 'change')
                                                }
                                                if (
                                                  !areSameIdList(
                                                    form.getFieldValue('search_tag_ids'),
                                                    nextSearchTagIds
                                                  )
                                                ) {
                                                  form.setFieldValue(
                                                    'search_tag_ids',
                                                    rememberStandaloneSearchTags(nextSearchTagIds)
                                                  )
                                                  void form.validateField(
                                                    'search_tag_ids',
                                                    'change'
                                                  )
                                                }
                                                setCollectionsWereEdited(true)
                                                void form.validateField('collections', 'change')
                                                setIsCollectionsDialogOpen(false)
                                              }}
                                              onCancel={() => setIsCollectionsDialogOpen(false)}
                                            />
                                          ) : null}
                                          <Typography variant="label" tone="muted">
                                            Need a new collection?{' '}
                                            <Link
                                              {...path.collections.create().link}
                                              className="font-medium underline underline-offset-2"
                                            >
                                              Create one in collections
                                            </Link>
                                            , then come back here.
                                          </Typography>
                                        </DialogContent>
                                      </Dialog>
                                    </Can>
                                  ) : null}
                                </div>
                              )}
                            </form.AppField>
                          </ClassificationRow>
                        </Can>
                      ) : null}

                      <form.AppField name="occasion_ids">
                        {(field) => (
                          <field.OccasionPicker
                            label={null}
                            options={merchandisingTagOptions}
                            value={selectedMerchandisingTagIds}
                            writeToField={false}
                            mode="multiple"
                            buttonLabel="Choose Occasions"
                            dialogDescription="Choose the occasions where this product should be eligible."
                            placeholder="No occasions selected yet."
                            selectedPillStyle="classification"
                            triggerClassName="px-3.5"
                            onValueChange={updateProductMerchandisingTags}
                            renderLayout={({ trigger, selectedSummary, dialog, fieldInfo }) => (
                              <ClassificationRow
                                className={classificationSubcardClass}
                                icon={Tags}
                                title="Occasions"
                                description="Power website filters and smart occasion collections."
                                action={trigger}
                              >
                                <div className="space-y-3">
                                  {selectedSummary}
                                  {fieldInfo}
                                  {dialog}
                                </div>
                              </ClassificationRow>
                            )}
                          />
                        )}
                      </form.AppField>

                      <ClassificationRow
                        icon={Search}
                        title="Find It Fast"
                        description="Help shoppers search and filter this product."
                      >
                        <form.AppField name="search_tag_ids">
                          {(field) => (
                            <ProductSearchTagPicker
                              key={productRole}
                              id={`${id}-search-tags`}
                              disabled={isReadOnly}
                              groups={customerSearchTagGroups}
                              suggestionGroups={searchTagGroups}
                              suggestionValue={selectedProductSearchTagIds}
                              isApplyingSuggestions={recommendSearchTagsMutation.isPending}
                              isSuggesting={recommendSearchTagsMutation.isPending}
                              onApplySuggestions={(nextTagIds) =>
                                applySuggestedSearchTags(field, nextTagIds)
                              }
                              onSuggest={suggestSearchTags}
                              value={selectedCustomerSearchTagIds}
                              onChange={(nextTagIds) =>
                                field.setValue(
                                  rememberStandaloneSearchTags([
                                    ...selectedSeasonIds,
                                    ...nextTagIds,
                                  ])
                                )
                              }
                            />
                          )}
                        </form.AppField>
                      </ClassificationRow>
                    </CardContent>
                  </Card>
                ) : null}

                {isAddOnProduct ? (
                  <Card
                    id={summarySectionIds.appliesTo}
                    className="min-w-0 scroll-mt-4 xl:col-span-2"
                  >
                    <CardHeader className="border-b border-border/60">
                      <CardTitle className="inline-flex items-center gap-2">
                        <Tags className="size-5" />
                        <span>Offer this Add-on</span>
                      </CardTitle>
                      <CardDescription className="max-w-3xl leading-5">
                        Add-on Collections connect this add-on to the product collections where
                        shoppers should see it.
                      </CardDescription>
                      {!isReadOnly ? (
                        <CardAction>
                          <CreateActionButton
                            context="inline"
                            size="sm"
                            variant="outline"
                            className="shrink-0 px-3.5"
                            onClick={() => setIsAddOnSetsDialogOpen(true)}
                          >
                            Choose Add-on Collections
                          </CreateActionButton>
                        </CardAction>
                      ) : null}
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <form.AppField name="add_on_sets">
                        {(field) => (
                          <div className="space-y-4">
                            {field.state.value?.length ? (
                              <div className="flex flex-wrap gap-2">
                                {field.state.value.map((set) => (
                                  <AddOnSetBadgeDropdown key={set.id} path={path} set={set} />
                                ))}
                              </div>
                            ) : (
                              <ClassificationEmptyState>
                                Not offered in any Add-on Collections yet.
                              </ClassificationEmptyState>
                            )}

                            <Dialog
                              open={isAddOnSetsDialogOpen}
                              onOpenChange={setIsAddOnSetsDialogOpen}
                            >
                              <DialogContent size="large">
                                <DialogHeader>
                                  <DialogTitle>Add-on Collections</DialogTitle>
                                  <DialogDescription>
                                    Choose the Add-on Collections that should include this item.
                                  </DialogDescription>
                                </DialogHeader>
                                {isAddOnSetsDialogOpen ? (
                                  <AddOnSetPicker
                                    organizationId={organizationId ?? undefined}
                                    initialData={field.state.value ?? []}
                                    onConfirm={(sets) => {
                                      field.setValue(sets)
                                      void form.validateField('add_on_sets', 'change')
                                      setIsAddOnSetsDialogOpen(false)
                                    }}
                                    onCancel={() => setIsAddOnSetsDialogOpen(false)}
                                  />
                                ) : null}
                                <DetailWell compact>
                                  <Typography variant="label" tone="muted">
                                    Need a new Add-on Collection?{` `}
                                    <Link
                                      {...path.addOnSets.create().link}
                                      className="font-medium underline underline-offset-2"
                                    >
                                      Create one in Add-on Collections
                                    </Link>
                                    , then come back here.
                                  </Typography>
                                </DetailWell>
                              </DialogContent>
                            </Dialog>
                          </div>
                        )}
                      </form.AppField>
                    </CardContent>
                  </Card>
                ) : null}

                <section className="min-w-0 space-y-5 rounded-xl border border-border bg-card p-6 xl:col-span-2">
                  <div className="space-y-1">
                    <div className="inline-flex items-center gap-2">
                      <Search className="size-5" />
                      <Typography variant="h4" className="block">
                        Google Preview
                      </Typography>
                    </div>
                    <Typography variant="supporting" className="block">
                      We will automatically use the product information for this unless you want to
                      manually change it.
                    </Typography>
                  </div>

                  <ProductSeoPreview
                    key={seoPreviewEditorSessionKey}
                    canViewCollections={canViewCollections}
                    form={form}
                    includeCollections={isSoldStandalone || isSubscriptionPackage}
                    isReadOnly={isReadOnly}
                    onUrlSlugManualEdit={() => {
                      urlSlugManuallyEditedRef.current = true
                    }}
                    organization={seoOrganization}
                    previewSource={seoPreviewSource}
                  />
                </section>
              </div>
            </div>
          </div>

          {!isReadOnly ? <SaveBar onReset={handleResetForm} /> : null}
        </form.Form>
      </form.AppForm>

      <Dialog open={isPresetDialogOpen} onOpenChange={setIsPresetDialogOpen}>
        <DialogContent size="small">
          <DialogHeader>
            <DialogTitle>Create Standard / Deluxe / Premium</DialogTitle>
            <DialogDescription>
              Set the Standard price once. Deluxe will be 50% higher, and Premium will be double the
              Standard price.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <StandaloneMoneyInput
                id="preset-standard-price"
                label="Standard Price"
                value={presetStandardPrice}
                onChange={setPresetStandardPrice}
              />
              {presetStandardPriceError ? (
                <Typography variant="label" tone="destructive">
                  {presetStandardPriceError}
                </Typography>
              ) : (
                <Typography variant="label" tone="muted">
                  Standard{' '}
                  {deferredPresetStandardPrice
                    ? formatCurrency(deferredPresetStandardPrice)
                    : '$0.00'}{' '}
                  · Deluxe{' '}
                  {deferredPresetStandardPrice
                    ? formatCurrency(deferredPresetPriceValue * 1.5)
                    : '$0.00'}{' '}
                  · Premium{' '}
                  {deferredPresetStandardPrice
                    ? formatCurrency(deferredPresetPriceValue * 2)
                    : '$0.00'}
                </Typography>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setIsPresetDialogOpen(false)
                  setPresetStandardPrice('')
                  setPresetStandardPriceError('')
                }}
              >
                Cancel
              </Button>
              <Button onClick={applyPresetVariants}>Create Variants</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ProductForm(props: ProductFormProps) {
  return <ProductFormSession key={props.initialData?.id ?? 'new'} {...props} />
}

function ProductFormSession({ initialData = null, ...props }: ProductFormProps) {
  const [session, setSession] = useState(() => ({
    product: initialData,
    revision: 0,
    source: initialData,
  }))

  let activeSession = session

  if (session.source !== initialData) {
    activeSession = {
      product: initialData,
      revision: session.revision + 1,
      source: initialData,
    }
    setSession(activeSession)
  }

  const handleSavedProduct = (product: SavedProductFormValues) => {
    setSession((currentSession) => ({
      ...currentSession,
      product,
      revision: currentSession.revision + 1,
    }))
  }

  return (
    <ProductFormInner
      key={activeSession.revision}
      {...props}
      initialData={activeSession.product}
      onSavedProduct={handleSavedProduct}
    />
  )
}

function StandaloneMoneyInput({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: ReactNode
  value: string
  onChange: (value: string) => void
}) {
  return (
    <MoneyInputControl
      fieldName={id}
      fieldValue={value}
      onFieldChange={onChange}
      id={id}
      label={label}
      type="hidden"
      placeholder="79.99"
      showFreeBadge={false}
      formatEmptyAsZero={false}
    />
  )
}

export default ProductForm
